// components/common/Background.jsx
import { useEffect, useMemo, useState } from "react";

export default function BackgroundBlobs({
  count = 6,
  minSize = 160,
  maxSize = 320,
  maxBlurPx = 100,
}) {
  const [viewport, setViewport] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    onResize();
    window.addEventListener("resize", onResize);
    // Use requestAnimationFrame throttling for resize for a potential minor boost
    // (though not strictly necessary for this simple use case)
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Use maxBlurPx as the blur radius for the box-shadow
  // We can simplify this since we no longer need the complex DPR calculation
  const blurRadius = maxBlurPx; 

  // Randomize every mount (Math.random) - unchanged from your original
  const blobs = useMemo(() => (
    Array.from({ length: count }).map(() => {
      // Use size as both the size of the element AND the spread of the shadow
      const size = minSize + Math.random() * (maxSize - minSize);
      const top = Math.random() * 85;
      const left = Math.random() * 85;
      const opacity = 0.06 + Math.random() * 0.06; // 0.06–0.12
      return { size, top, left, opacity };
    })
  ), [count, minSize, maxSize]);

  const blur = Math.min(maxBlurPx, Math.max(40, Math.round((viewport.w + viewport.h) / 60)));
  const filterRadius = Math.min(maxBlurPx, Math.max(40, Math.round((viewport.w + viewport.h) / 60)));

  return (
    <div 
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
      aria-hidden
    >
      {blobs.map((b, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-ui-light-detail dark:bg-ui-dark-detail"
          style={{
            top: `${b.top}%`,
            left: `${b.left}%`,
            width: `${b.size}px`,
            height: `${b.size}px`,
            opacity: b.opacity,
            
            // REVERTED to filter: blur() for visual quality
            filter: `blur(${blur}px)`,
            
            // ADDED performance hints for the browser
            transform: "translate3d(-10%, -10%, 0)", // Use 3D transform for hardware acceleration
            willChange: "transform, filter, opacity",
            contain: "layout paint style", // Helps browser limit scope of repaint
          }}
        />
      ))}
    </div>
  );
}